/* The auth shell, from the owner-approved D3 poster mockup's `.shell` / `.pane` / `.photo-pane` /
 * `.frame` / `.cap*` rules (`docs/design/rejestracja-premium-2026-09-21/`).
 * Every value is a KANON token reference — Stylelint fails the build on a literal. */

.shell {
  /* The progress hairline is absolute across the whole page, so this is its containing block. The
   * only other absolutely positioned boxes under here are the photograph and the frame's scrim, and
   * both already sit inside `.frame`, which is positioned itself. */
  position: relative;
  display: grid;

  /* Two balanced zones, the mockup's own `1fr .78fr`.
   * A grid track is a layout decision, not a design token (see stylelint.config.js). */
  grid-template-columns: 1fr 0.78fr;
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
}

.pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: var(--space-10) var(--space-12);
}

.brand {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-head);
  font-size: var(--fs-brand);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

/* SIZE ONLY. The mark's treatment — the asset, the mask and the theme-adaptive fill — is
 * `shared/ui/brandMark.module.css`, which the element also wears, and nothing here may restate it. */
.mark {
  width: var(--brand-mark);
  height: var(--brand-mark);
}

/* Vertically centred, not bottom-anchored. Every auth screen in KANON sits at the bottom edge
 * because that is thumb reach; at a desk it is just a long way from the eye. */
.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: var(--space-8) var(--space-0);
}

/* CENTRED in its pane, horizontally as well as vertically (owner decision, 21.09.2026).
 *
 * The D3 poster rebuild left this column flush against the pane's left padding, because the head
 * was a wider measure than the fields and the empty right-hand side was the margin the step
 * indicator stood in like a folio. That indicator is gone — the ember hairline across the top of
 * the page carries the progress on its own — so the margin held nothing, and a 520px column pinned
 * to the left of an 850px pane read as a phone screen pasted into a corner.
 *
 * `margin-inline: auto` over the head measure, not over the pane: the column is centred as a block
 * and keeps both of its own measures, so the head still runs wider than the controls and the type
 * inside it stays left-aligned. Without the max-width the column would stretch to the pane and
 * centring would mean nothing.
 *
 * It carries no animation. The step-to-step motion is `authStepEntrance` on the blocks inside it —
 * one system, not two, and the only one of the two that can carry a direction. */
.form {
  width: 100%;
  max-width: var(--w-auth-head);
  margin-inline: auto;
}

/* The editorial measure: the head, the footer and the role cards share it, the fields do not.
 *
 * `margin-inline: auto` is here for the FOOTER, which is the one block of the column that sits
 * outside `.form` — it hangs at the bottom of the pane, below the vertically-centred body, and so
 * has to be centred against the same content box itself. On the head it is inert: that block
 * already fills the centred column exactly. */
.head,
.footer {
  width: var(--w-auth-head);
  max-width: 100%;
  margin-inline: auto;
}

.title {
  margin: var(--space-0);
  font-family: var(--font-head);

  /* The mockup's 36px, at the nearest token this system holds (34px). */
  font-size: var(--fs-empty);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  letter-spacing: var(--ls-display);
  color: var(--text);
  text-wrap: balance;
}

.subtitle {
  /* `--text-2`, never `--text-3`: on light surfaces the tertiary ink measures 2.17–2.80 and fails
   * AA everywhere, so it carries nothing that has to be read (docs/design/README.md). */
  max-width: 34ch;
  margin: var(--space-10px) var(--space-0) var(--space-7);
  font-family: var(--font-body);
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text-2);
}

/* The two measures a screen's own content can stand on, and the mockup draws both: the controls run
 * at `--w-form` and the role cards run the head's full width, because choosing a role is the whole
 * of that step and two tall posters are what it is drawn as. Which one a screen takes is its own
 * statement (`AuthShell`'s `measure`), and neither number leaves this file. */
.fields {
  width: var(--w-form);
  max-width: 100%;
}

.wide {
  width: var(--w-auth-head);
  max-width: 100%;
}

/* The action row under a screen's copy. Owned by the shell because the form column's rhythm is the
 * shell's concern — two screens declaring the same one-line rule is exactly the near-duplicate drift
 * the token discipline exists to prevent. */
.actions {
  margin-top: var(--space-4);
}

/* ── The poster pane ──────────────────────────────────────────────────────────────────────────
 * `--photo` is the graded plate the mockup ships. It is no longer only a fallback: it is the paper
 * the frame and the caption stand on, and the 40px around the frame is where it shows. */
.photo {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: var(--space-10);
  overflow: hidden;
  background: var(--photo);
}

/* The frame. It takes whatever height the caption leaves it, which is what keeps the caption on the
 * plate at every window instead of riding up onto the picture.
 *
 * The ground under the photograph is the plate again rather than a flat black: it is
 * theme-independent, it is already near-black at both ends, and it is what shows for the moment
 * before a bundled asset decodes — the same job `photoBox`'s neutral ground does for a network
 * image (`src/shared/AGENTS.md`).
 *
 * The hero inside it is an ELEMENT, not a pseudo — `shared/ui/HeroPhoto.tsx`, which owns the
 * `<img>`, its grade and the cross-fade between two of them. ONE image per photograph for both
 * themes: the grade plus the scrim already do the theme's work, and the plate is near-black in
 * both. */
.frame {
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: var(--r-card);
  overflow: hidden;
  background: var(--photo);
}

/* The scrim, INSIDE the frame. A pseudo-element paints after every child of its own element, so it
 * stays over the photograph without either side declaring a `z-index`.
 *
 * It is no longer holding white type up over a bright window — the tag and the headline moved onto
 * the plate below — but it stays: the mockup keeps it, and it is what stops a blown-out doorway
 * from being the brightest thing on the page. */
.frame::after {
  position: absolute;
  inset: var(--space-0);
  content: "";
  background: var(--photo-scrim);
}

/* The tag and the hero line, on the plate UNDER the frame — the mockup's `.cap`. This is the whole
 * of what "poster, not wallpaper" means: no typography ever lies on a person's face. */
.caption {
  flex: none;
  margin-top: var(--space-22px);
}

.captionTag {
  margin-bottom: var(--space-10px);
  font-family: var(--font-body);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-eyebrow);
  text-transform: uppercase;
  color: var(--text-2-on-photo);
}

.captionLine {
  max-width: 19ch;
  font-family: var(--font-head);

  /* The mockup's 25px / 1.16 / -0.025em, at the nearest tokens this system holds. */
  font-size: var(--fs-state);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-plan-hero);
  letter-spacing: var(--ls-title);
  color: var(--text-on-photo);
}

/* The accent clause. It takes the caption's own ink: no gradient and no orange on the sign-in and
 * sign-up caption (owner, 4 November 2026). The box is kept (`inline-block` and the 5px under the
 * baseline) so the caption's height and wrap are the same as before and nothing below it moves. */
.captionAccent {
  display: inline-block;
  padding-block-end: var(--space-5px);
}

/* A short window — a 720p laptop once the browser's chrome is gone. The mockup's own
 * `@media (max-height:700px)` block, which trades the editorial scale for the fit. */
@media (height <= 700px) {
  .pane {
    padding: var(--space-7) var(--space-8);
  }

  .body {
    padding: var(--space-5) var(--space-0);
  }

  .title {
    font-size: var(--fs-h1);
  }

  .photo {
    padding: var(--space-7);
  }

  .captionLine {
    font-size: var(--fs-h2);
  }
}

/* Below the split's comfortable width the photo pane stops being texture and starts being a squeeze,
 * so it goes and the form takes the whole canvas. The breakpoint is a literal because `var()` does
 * not resolve inside a media query — there is no token form of this value.
 *
 * LAST in the file, and that placement is load-bearing: a media query adds no specificity, so this
 * block declared above `.photo { display: flex }` loses to it and the pane stays visible while the
 * grid has already collapsed. Caught in the browser at a 757px window, not in review. */
@media (width <= 900px) {
  .shell {
    grid-template-columns: 1fr;
  }

  .photo {
    display: none;
  }
}
